import React, { useEffect, useRef, useState } from 'react';
import { FiDownload, FiMusic, FiPause, FiPlay } from 'react-icons/fi';
import { fullUrl } from '../../api';
import { formatTime } from '../../utils/audio';
import { releasePlayback, requestPlayback } from '../../utils/mediaPlayback';
import { BLUE, NAVY } from './brand';

/**
 * Music / voice player for community posts, comments and chat.
 *
 * · **Play only on a click** — an mp3 never starts by itself (no autoplay), so
 *   scrolling the feed stays silent.
 * · **One mp3 at a time** — starting a track takes the page's single playback
 *   slot (`utils/mediaPlayback`), which pauses whatever played before: another
 *   mp3, a feed video or a reel. Scroll a video into view and the mp3 pauses.
 *
 * With `cover` + `round` the cover image is shown as a **100% round disc that
 * spins 360° while the audio is playing** (the vinyl effect the user asked for).
 * Without a cover it shows a music icon on a blue disc (still spinning).
 */
export default function AudioPlayer({
  src, title = '', cover = '', round = false, size = 128, compact = false,
}) {
  const audio = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const url = src && src.startsWith('http') ? src : fullUrl(src);
  const coverUrl = cover ? (cover.startsWith('http') ? cover : fullUrl(cover)) : '';

  useEffect(() => {
    const el = audio.current;
    if (!el) return undefined;
    const onTime = () => setTime(el.currentTime || 0);
    const onMeta = () => setDuration(el.duration || 0);
    const onEnd = () => { setPlaying(false); setTime(0); releasePlayback(el); };
    // A pause can come from the user … or from another media taking the slot.
    const onPause = () => { if (!el.seeking) setPlaying(false); releasePlayback(el); };
    const onPlay = () => setPlaying(true);
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('ended', onEnd);
    el.addEventListener('pause', onPause);
    el.addEventListener('play', onPlay);
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('loadedmetadata', onMeta);
      el.removeEventListener('ended', onEnd);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('play', onPlay);
      el.pause();
      releasePlayback(el);
    };
  }, [url]);

  /** Click to play: take the single playback slot, then start. */
  const toggle = (e) => {
    if (e) e.stopPropagation();
    const el = audio.current;
    if (!el) return;
    if (el.paused) {
      requestPlayback(el, () => { try { el.pause(); } catch (err) { /* ignore */ } });
      const started = el.play();
      if (started && started.catch) started.catch(() => releasePlayback(el));
    } else {
      el.pause();
      releasePlayback(el);
    }
  };

  const seek = (e) => {
    e.stopPropagation();
    const el = audio.current;
    if (!el || !duration) return;
    const box = e.currentTarget.getBoundingClientRect();
    el.currentTime = ((e.clientX - box.left) / box.width) * duration;
    setTime(el.currentTime);
  };

  const pct = duration ? Math.min(100, (time / duration) * 100) : 0;

  // --- chat / comment bubble: one compact row ---------------------------------
  if (compact) {
    return (
      <span className="flex items-center gap-2 min-w-[190px]">
        <audio ref={audio} src={url} preload="metadata" />
        <button
          type="button"
          onClick={toggle}
          className="w-9 h-9 rounded-full flex items-center justify-center text-white shrink-0"
          style={{ background: BLUE }}
          aria-label={playing ? 'pause' : 'play'}
        >
          {playing ? <FiPause className="w-4 h-4" /> : <FiPlay className="w-4 h-4 ml-0.5" />}
        </button>
        <span className="flex-1 min-w-[80px] h-1.5 rounded-full bg-gray-200/70 dark:bg-gray-700" onClick={seek}>
          <span className="block h-full rounded-full" style={{ width: `${pct}%`, background: BLUE }} />
        </span>
        <span className="text-[10px] tabular-nums opacity-80 shrink-0">
          {formatTime(time || duration)}
        </span>
      </span>
    );
  }

  // --- full player (posts) ----------------------------------------------------
  return (
    <div className="mt-3 flex items-center gap-3 sm:gap-4 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-800/40 p-3">
      <audio ref={audio} src={url} preload="metadata" />

      <button
        type="button"
        onClick={toggle}
        className={`audio-cover relative ${playing ? 'audio-playing' : ''}`}
        style={{ width: size, height: size }}
        aria-label={playing ? 'pause' : 'play'}
      >
        {round && coverUrl ? (
          <img src={coverUrl} alt={title || 'cover'} className="w-full h-full object-cover" />
        ) : (
          <span className="w-full h-full flex items-center justify-center text-white" style={{ background: NAVY }}>
            <FiMusic className="w-1/3 h-1/3" />
          </span>
        )}
        <span className="absolute inset-0 flex items-center justify-center bg-black/25 opacity-0 hover:opacity-100 transition">
          {playing ? <FiPause className="w-6 h-6 text-white" /> : <FiPlay className="w-6 h-6 text-white" />}
        </span>
      </button>

      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold truncate" style={{ color: BLUE }}>
          {title || 'Audio'}
        </p>
        <div className="mt-1.5 flex items-center gap-2">
          <button
            type="button"
            onClick={toggle}
            className="w-7 h-7 rounded-full flex items-center justify-center text-white shrink-0"
            style={{ background: BLUE }}
            aria-label="play"
          >
            {playing ? <FiPause className="w-3.5 h-3.5" /> : <FiPlay className="w-3.5 h-3.5 ml-0.5" />}
          </button>
          <span className="flex-1 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700" onClick={seek}>
            <span className="block h-full rounded-full" style={{ width: `${pct}%`, background: BLUE }} />
          </span>
          <span className="text-[11px] text-gray-500 tabular-nums shrink-0">
            {formatTime(time)} / {formatTime(duration)}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-3 text-[11px] text-gray-400">
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            download
            className="hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            <FiDownload className="w-3 h-3 inline" /> mp3
          </a>
        </div>
      </div>
    </div>
  );
}

